/* Princess Inspired — vanilla JS for the prototype's interactive bits.
   Layered on top of Dawn's existing JS — only handles things that aren't already in Dawn. */

(function () {
  'use strict';

  // ─── Announcement bar rotator (when 2+ messages + auto-rotate enabled) ──
  (function setupAnnouncementRotator() {
    const wrap = document.querySelector('[data-pi-announcement]');
    if (!wrap || wrap.dataset.rotate !== 'true') return;
    const msgs = wrap.querySelectorAll('.pi-announcement__msg');
    if (msgs.length < 2) return;
    const speed = (parseInt(wrap.dataset.speed, 10) || 6) * 1000;
    let idx = 0;
    setInterval(() => {
      msgs[idx].classList.remove('is-active');
      idx = (idx + 1) % msgs.length;
      msgs[idx].classList.add('is-active');
    }, speed);
  })();

  // ─── Header: scroll state + measure height for body padding ──
  const header = document.querySelector('.pi-header');
  if (header) {
    const onScroll = () => header.classList.toggle('is-scrolled', window.scrollY > 4);
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();

    // Header is position:fixed; body padding-top = actual header height (handles announcement on/off, mobile, font load).
    const setHeight = () => {
      document.documentElement.style.setProperty('--pi-header-h', header.offsetHeight + 'px');
    };
    setHeight();
    window.addEventListener('resize', setHeight);
    if (document.fonts && document.fonts.ready) document.fonts.ready.then(setHeight);
  }

  // ─── Mobile menu open/close ───────────────────────────────────
  const menu = document.querySelector('.pi-mobile-menu');
  const menuOpenBtns = document.querySelectorAll('[data-pi-menu-open]');
  const menuCloseBtns = document.querySelectorAll('[data-pi-menu-close]');
  menuOpenBtns.forEach(b => b.addEventListener('click', () => menu && menu.classList.add('is-open')));
  menuCloseBtns.forEach(b => b.addEventListener('click', () => menu && menu.classList.remove('is-open')));

  // ─── Cart drawer open/close ───────────────────────────────────
  const openCart  = () => {
    const drawer = document.querySelector('.pi-cart-drawer');
    const backdrop = document.querySelector('.pi-cart-backdrop');
    if (drawer)   drawer.classList.add('is-open');
    if (backdrop) backdrop.classList.add('is-open');
    document.body.style.overflow = 'hidden';
  };
  const closeCart = () => {
    const drawer = document.querySelector('.pi-cart-drawer');
    const backdrop = document.querySelector('.pi-cart-backdrop');
    if (drawer)   drawer.classList.remove('is-open');
    if (backdrop) backdrop.classList.remove('is-open');
    document.body.style.overflow = '';
  };
  // Delegate so dynamically replaced drawer content still works
  document.addEventListener('click', (e) => {
    const open = e.target.closest('[data-pi-cart-open]');
    if (open) { e.preventDefault(); openCart(); return; }
    const close = e.target.closest('[data-pi-cart-close]');
    if (close) { e.preventDefault(); closeCart(); return; }
  });
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') closeCart();
  });

  // ─── Cart drawer qty +/- buttons (delegated) ──────────────────
  document.addEventListener('click', async (e) => {
    const btn = e.target.closest('[data-pi-qty-change]');
    if (!btn) return;
    e.preventDefault();
    const lineKey = btn.dataset.lineKey;
    const qty = parseInt(btn.dataset.qty, 10);
    btn.disabled = true;
    try {
      await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ id: lineKey, quantity: qty })
      });
      await refreshCart();
    } catch (err) {
      console.error('[PI] qty change failed', err);
    } finally {
      btn.disabled = false;
    }
  });

  // ─── Princess gallery scroll affordance ───────────────────────
  document.querySelectorAll('[data-pi-gallery]').forEach(gal => {
    const rail = gal.querySelector('[data-rail]');
    if (!rail) return;
    const variant = gal.dataset.piGallery; // fade | counter | peek
    const arrows = gal.querySelectorAll('[data-arrow]');
    const counter = gal.querySelector('[data-counter]');
    const progress = gal.querySelector('[data-progress-fill]');

    const update = () => {
      const max = rail.scrollWidth - rail.clientWidth;
      const left = rail.scrollLeft;
      const tiles = rail.querySelectorAll(':scope > button, :scope > [data-tile]');
      let visibleIdx = 0;
      for (let i = 0; i < tiles.length; i++) {
        const t = tiles[i];
        if (t.offsetLeft + t.offsetWidth / 2 >= left + 40) { visibleIdx = i; break; }
      }
      gal.dataset.atStart = String(left <= 2);
      gal.dataset.atEnd = String(left >= max - 2);
      if (counter) counter.querySelector('b').textContent = String(Math.min(visibleIdx + 1, tiles.length));
      if (progress && max > 0) progress.style.left = (left / max * 75) + '%';
    };

    arrows.forEach(a => a.addEventListener('click', () => {
      const dir = a.dataset.arrow === 'l' ? -1 : 1;
      rail.scrollBy({ left: dir * rail.clientWidth * 0.7, behavior: 'smooth' });
    }));
    rail.addEventListener('scroll', update, { passive: true });
    window.addEventListener('resize', update);
    update();
  });

  // ─── Hero carousel slider ─────────────────────────────────────
  document.querySelectorAll('[data-pi-hero-slider]').forEach(slider => {
    const track = slider.querySelector('[data-track]');
    if (!track) return;
    const slides = track.querySelectorAll('[data-slide-index]');
    if (slides.length <= 1) return;
    const arrows = slider.querySelectorAll('[data-arrow]');
    const dots = slider.querySelectorAll('[data-go-to]');
    const autoAdvance = slider.dataset.autoAdvance === 'true';
    const speedSeconds = parseInt(slider.dataset.speed, 10) || 6;
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    let currentIndex = 0;
    let autoTimer = null;

    const updateDots = () => {
      dots.forEach((d, i) => d.classList.toggle('is-active', i === currentIndex));
    };
    const goTo = (idx) => {
      const clamped = ((idx % slides.length) + slides.length) % slides.length;
      track.scrollTo({ left: clamped * track.clientWidth, behavior: 'smooth' });
      currentIndex = clamped;
      updateDots();
    };
    const updateFromScroll = () => {
      const idx = Math.round(track.scrollLeft / track.clientWidth);
      if (idx !== currentIndex && idx >= 0 && idx < slides.length) {
        currentIndex = idx;
        updateDots();
      }
    };

    function startAuto() {
      if (!autoAdvance || reducedMotion || autoTimer) return;
      autoTimer = setInterval(() => goTo(currentIndex + 1), speedSeconds * 1000);
    }
    function stopAuto() { if (autoTimer) { clearInterval(autoTimer); autoTimer = null; } }
    function restartAuto() { stopAuto(); startAuto(); }

    arrows.forEach(a => a.addEventListener('click', () => {
      const dir = a.dataset.arrow === 'l' ? -1 : 1;
      goTo(currentIndex + dir);
      restartAuto();
    }));
    dots.forEach(d => d.addEventListener('click', () => {
      goTo(parseInt(d.dataset.goTo, 10));
      restartAuto();
    }));
    track.addEventListener('scroll', updateFromScroll, { passive: true });
    window.addEventListener('resize', () => goTo(currentIndex));
    slider.addEventListener('mouseenter', stopAuto);
    slider.addEventListener('mouseleave', startAuto);
    document.addEventListener('visibilitychange', () => {
      if (document.hidden) stopAuto(); else startAuto();
    });

    startAuto();
  });

  // ─── Policy page TOC: build from <h2> tags, scroll-spy active state ───
  (function setupPolicyTOC() {
    const toc = document.querySelector('[data-pi-policy-toc]');
    const article = document.querySelector('[data-pi-policy-article]');
    if (!toc || !article) return;
    const headings = Array.from(article.querySelectorAll('h2'));
    if (headings.length === 0) return;

    headings.forEach((h, i) => {
      if (!h.id) {
        const slug = (h.textContent || '').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
        h.id = slug || ('section-' + (i + 1));
      }
      const a = document.createElement('a');
      a.href = '#' + h.id;
      const num = String(i + 1).padStart(2, '0');
      const labelText = h.textContent.replace(/^\d+\s+/, '').trim();
      a.innerHTML = '<span class="num">' + num + '</span><span>' + labelText + '</span>';
      toc.appendChild(a);
    });

    const links = Array.from(toc.querySelectorAll('a'));
    const setActive = () => {
      const offset = (parseInt(getComputedStyle(document.documentElement).getPropertyValue('--pi-header-h'), 10) || 110) + 120;
      let activeIdx = 0;
      for (let i = 0; i < headings.length; i++) {
        if (headings[i].getBoundingClientRect().top < offset) activeIdx = i;
      }
      links.forEach((l, i) => l.classList.toggle('is-active', i === activeIdx));
    };
    window.addEventListener('scroll', setActive, { passive: true });
    setActive();
  })();

  // ─── FAQ help-center category tabs ─────────────────────────────
  // Click a pill → activate it + show its panel, hide others.
  document.querySelectorAll('[data-pi-faq-tab]').forEach(tab => {
    tab.addEventListener('click', () => {
      const id = tab.dataset.piFaqTab;
      const tabs = tab.closest('.pi-faq').querySelectorAll('[data-pi-faq-tab]');
      const panels = tab.closest('.pi-faq').querySelectorAll('[data-pi-faq-panel]');
      tabs.forEach(t => {
        const on = t.dataset.piFaqTab === id;
        t.classList.toggle('is-active', on);
        t.setAttribute('aria-selected', on ? 'true' : 'false');
      });
      panels.forEach(p => p.classList.toggle('is-active', p.dataset.piFaqPanel === id));
    });
  });

  // ─── Contact page topic chips → hidden form field ─────────────
  // Single-select chip group; clicking a chip writes its value to the contact form's
  // hidden contact[Topic] input so it shows up in the email submission.
  document.querySelectorAll('[data-pi-topic]').forEach(chip => {
    chip.addEventListener('click', () => {
      document.querySelectorAll('[data-pi-topic]').forEach(b => b.classList.remove('is-active'));
      chip.classList.add('is-active');
      const value = chip.dataset.topicValue || '';
      const input = document.querySelector('[data-pi-topic-input]');
      if (input) input.value = value;
    });
  });

  // ─── Quick-add from product card (single-variant only) ──────────
  // Card-product renders <button data-pi-quick-add data-variant-id="X"> when
  // the product has only a default variant. Click → AJAX add → open cart drawer.
  document.addEventListener('click', async (e) => {
    const btn = e.target.closest('[data-pi-quick-add]');
    if (!btn) return;
    e.preventDefault();
    e.stopPropagation();
    if (btn.disabled) return;
    const variantId = btn.dataset.variantId;
    if (!variantId) return;
    const originalLabel = btn.textContent;
    btn.disabled = true;
    btn.textContent = 'Adding…';
    try {
      const res = await fetch(window.theme?.routes?.cart_add_url || '/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/javascript' },
        body: JSON.stringify({ id: parseInt(variantId, 10), quantity: 1 }),
      });
      if (!res.ok) throw new Error('add failed');
      // Refresh the cart drawer (uses the same pattern as the buy box ATC)
      const sectionRes = await fetch('/?section_id=header');
      const html = await sectionRes.text();
      const div = document.createElement('div');
      div.innerHTML = html;
      const newDrawer = div.querySelector('.pi-cart-drawer-root');
      const oldDrawer = document.querySelector('.pi-cart-drawer-root');
      if (newDrawer && oldDrawer) oldDrawer.replaceWith(newDrawer);
      openCart();
    } catch (err) {
      btn.textContent = 'Could not add';
      setTimeout(() => { btn.textContent = originalLabel; btn.disabled = false; }, 1800);
      return;
    }
    btn.textContent = originalLabel;
    btn.disabled = false;
  });

  // ─── PDP variant changes (bundle + color + size) → section refresh ────────
  // All option/bundle button clicks compute the matching variant ID and fetch
  // the section HTML with ?variant=X&section_id=Y, then swap the [data-pi-pdp]
  // wrapper. This ensures every server-rendered piece (active state, "What's
  // included" panel, prices, thumbnails, etc.) updates correctly.
  let pdpRefreshing = false;
  async function refreshPdpForVariant(variantId) {
    if (pdpRefreshing || !variantId) return;
    const pdp = document.querySelector('[data-pi-pdp]');
    if (!pdp) return;
    const sectionId = pdp.dataset.sectionId;
    const productUrl = pdp.dataset.productUrl;
    if (!sectionId || !productUrl) return;
    pdpRefreshing = true;

    // Update browser URL so refresh + share keeps the variant
    try {
      const u = new URL(window.location.href);
      u.searchParams.set('variant', variantId);
      window.history.replaceState({}, '', u);
    } catch (_) {}

    try {
      const res = await fetch(`${productUrl}?variant=${variantId}&section_id=${sectionId}`, {
        headers: { 'Accept': 'text/html' }
      });
      if (!res.ok) throw new Error('Section render failed: ' + res.status);
      const html = await res.text();
      const tmp = document.createElement('div');
      tmp.innerHTML = html;
      const newPdp = tmp.querySelector('[data-pi-pdp]');
      if (newPdp) {
        // Preserve scroll position
        const y = window.scrollY;
        pdp.replaceWith(newPdp);
        // Re-execute any inline <script> tags inside the new chunk (PI_PRODUCT_VARIANTS, etc.)
        tmp.querySelectorAll('script').forEach(orig => {
          const s = document.createElement('script');
          s.textContent = orig.textContent;
          document.head.appendChild(s);
          s.remove();
        });
        window.scrollTo({ top: y });
      }
    } catch (err) {
      console.error('[PI] PDP refresh failed', err);
    } finally {
      pdpRefreshing = false;
    }
  }

  function matchVariantFromOptions(name, value) {
    const opts = (window.PI_PRODUCT_OPTIONS || []).slice();
    if (!opts.length) return null;
    // Read currently-selected values from active buttons in the buybox
    const current = {};
    document.querySelectorAll('[data-pi-pdp] [data-option-name].is-active').forEach(b => {
      const n = b.dataset.optionName, v = b.dataset.optionValue;
      if (n && v) current[n] = v;
    });
    current[name] = value; // override with the just-clicked value
    const variants = window.PI_PRODUCT_VARIANTS || [];
    const match = variants.find(v => (v.options || []).every((val, i) => {
      const optName = opts[i];
      return !optName || current[optName] === val || current[optName] == null;
    }));
    return match ? match.id : null;
  }

  // Delegated: thumbnail "tap to preview" — must run BEFORE the card handler
  // because the thumb sits inside an active bundle card and we need to intercept
  // the bubble. Honors data-detail-mode on the picker (inline | modal | link).
  document.addEventListener('click', (e) => {
    const thumb = e.target.closest('[data-pi-bundle-thumb]');
    if (!thumb) return;
    const card = thumb.closest('[data-bundle-card]');
    // If the thumb is on an INACTIVE card, fall through and let the card handler
    // select that bundle (don't preview a non-selected tier's items).
    if (!card || !card.classList.contains('is-active')) return;

    e.preventDefault();
    // stopImmediatePropagation is critical: both handlers are on `document`, and
    // plain stopPropagation() doesn't prevent the card handler from also firing
    // and triggering a section refresh that wipes the peek we're about to open.
    e.stopImmediatePropagation();

    const picker = thumb.closest('[data-pi-bundle-picker]');
    const mode = (picker && picker.dataset.detailMode) || 'inline';

    if (mode === 'link') {
      const href = thumb.dataset.itemUrl;
      if (href) window.location.href = href;
      return;
    }

    // 'modal' mode falls back to inline for now (modal isn't built — would need a separate snippet).
    // Inline peek: fill the existing peek drawer with this thumb's data.
    const peek = card.querySelector('[data-pi-peek]');
    if (!peek) return;

    const sameThumbOpen = peek.dataset.itemKey === thumb.dataset.itemKey && peek.style.display !== 'none';
    if (sameThumbOpen) {
      peek.style.display = 'none';
      peek.dataset.itemKey = '';
      return;
    }

    peek.dataset.itemKey = thumb.dataset.itemKey || '';

    // Fill peek content from the thumb's data attrs
    const fillText = (sel, val) => {
      const el = peek.querySelector(sel);
      if (el) el.textContent = val || '';
    };
    fillText('[data-peek-label]', thumb.dataset.itemLabel);
    fillText('[data-peek-desc]',  thumb.dataset.itemDesc);

    // Glyph or image — let CSS handle sizing (responsive across desktop + mobile)
    const media = peek.querySelector('[data-peek-media]');
    if (media) {
      const imgUrl = thumb.dataset.itemImage;
      if (imgUrl) {
        const safeAlt = (thumb.dataset.itemLabel || '').replace(/"/g, '&quot;');
        media.innerHTML = '<img src="' + imgUrl + '" alt="' + safeAlt + '" loading="lazy">';
      } else {
        media.innerHTML = '<span style="font-size:36px;">' + (thumb.dataset.itemGlyph || '✨') + '</span>';
      }
    }

    // "Shop individually for $X →" link — combines price + CTA into one element
    const link = peek.querySelector('[data-peek-link]');
    if (link) {
      if (thumb.dataset.itemUrl) {
        const price = thumb.dataset.itemPrice;
        link.textContent = price ? 'Shop individually for ' + price + ' →' : 'Shop individually →';
        link.href = thumb.dataset.itemUrl;
        link.hidden = false;
      } else {
        link.hidden = true;
      }
    }

    peek.style.display = 'grid';
  });

  // Delegated: peek close button
  document.addEventListener('click', (e) => {
    const closeBtn = e.target.closest('[data-peek-close]');
    if (!closeBtn) return;
    e.preventDefault();
    e.stopImmediatePropagation();
    const peek = closeBtn.closest('[data-pi-peek]');
    if (peek) {
      peek.style.display = 'none';
      peek.dataset.itemKey = '';
    }
  });

  // Delegated: clicks anywhere on the bundle card or its variant button
  document.addEventListener('click', (e) => {
    // Don't hijack genuine link navigation inside a card (e.g. "Shop individually"
    // link in the peek drawer). If the user clicked an <a href> let the browser take it.
    const anchor = e.target.closest('a[href]');
    if (anchor && anchor.getAttribute('href') && anchor.getAttribute('href') !== '#') return;

    const bundleBtn = e.target.closest('[data-bundle-variant-id]');
    if (bundleBtn) {
      e.preventDefault();
      const id = bundleBtn.dataset.bundleVariantId;
      if (id) refreshPdpForVariant(id);
      return;
    }
    const card = e.target.closest('[data-bundle-card]');
    if (card) {
      // Click on inactive card body → select its first variant
      const innerBtn = card.querySelector('[data-bundle-variant-id]');
      if (innerBtn) {
        e.preventDefault();
        const id = innerBtn.dataset.bundleVariantId;
        if (id) refreshPdpForVariant(id);
      }
      return;
    }
  });

  // Delegated: clicks on color/size option buttons
  document.addEventListener('click', (e) => {
    const optBtn = e.target.closest('[data-option-name][data-option-value]');
    if (!optBtn) return;
    // Skip if it's a bundle button (handled above)
    if (optBtn.hasAttribute('data-bundle-variant-id')) return;
    e.preventDefault();
    const name = optBtn.dataset.optionName;
    const value = optBtn.dataset.optionValue;
    // Mark active immediately for snappy UX
    document.querySelectorAll(`[data-option-name="${CSS.escape(name)}"]`).forEach(b => b.classList.remove('is-active'));
    optBtn.classList.add('is-active');
    const colorName = document.querySelector('[data-color-name]');
    if (colorName && /color|colour/i.test(name)) colorName.textContent = value;
    // Compute new variant + refresh
    const id = matchVariantFromOptions(name, value);
    if (id) refreshPdpForVariant(id);
  });

  // ─── Storybook FAQ accordion (delegated, multi-open) ──────────
  document.addEventListener('click', (e) => {
    const btn = e.target.closest('[data-faq-btn]');
    if (!btn) return;
    e.preventDefault();
    const row = btn.closest('[data-faq-row]');
    const panel = row && row.querySelector('[data-faq-panel]');
    if (!row || !panel) return;
    const isOpen = row.classList.toggle('is-open');
    btn.setAttribute('aria-expanded', String(isOpen));
    if (isOpen) panel.removeAttribute('hidden');
    else panel.setAttribute('hidden', '');
  });

  // ─── Accordion (PDP details/story/care/ship) — delegated ──────
  document.addEventListener('click', (e) => {
    const btn = e.target.closest('[data-row-btn]');
    if (!btn) return;
    const row = btn.closest('[data-row]');
    const group = btn.closest('[data-pi-accordion]');
    if (!row || !group) return;
    const panel = row.querySelector('[data-row-panel]');
    const wasOpen = panel && panel.style.display !== 'none';
    // Close all rows
    group.querySelectorAll('[data-row]').forEach(r => {
      const p = r.querySelector('[data-row-panel]');
      const ip = r.querySelector('[data-row-icon-plus]');
      const im = r.querySelector('[data-row-icon-minus]');
      if (p) p.style.display = 'none';
      if (ip) ip.style.display = 'inline-flex';
      if (im) im.style.display = 'none';
    });
    // Open this row if it was closed
    if (panel && !wasOpen) {
      panel.style.display = 'block';
      const ip = row.querySelector('[data-row-icon-plus]');
      const im = row.querySelector('[data-row-icon-minus]');
      if (ip) ip.style.display = 'none';
      if (im) im.style.display = 'inline-flex';
    }
  });

  // ─── PDP form: AJAX add-to-cart (delegated, survives section refresh) ───
  document.addEventListener('submit', async (e) => {
    const form = e.target.closest('[data-pi-product-form]');
    if (!form) return;
    e.preventDefault();
    const submitBtn = form.querySelector('button[type="submit"]');
    const labelEl = form.querySelector('[data-pi-atc-label]');
    const originalLabel = labelEl ? labelEl.innerHTML : 'Add to bag';
    if (submitBtn) submitBtn.disabled = true;
    if (labelEl) labelEl.textContent = 'Adding…';
    try {
      const fd = new FormData(form);
      const cartAddUrl = (window.theme && window.theme.routes && window.theme.routes.cart_add_url) || '/cart/add.js';
      const res = await fetch(cartAddUrl, {
        method: 'POST',
        headers: { 'Accept': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: fd
      });
      const data = await res.json();
      if (!res.ok) {
        if (labelEl) labelEl.textContent = data.description || data.message || 'Could not add';
        setTimeout(() => { if (labelEl) labelEl.innerHTML = originalLabel; if (submitBtn) submitBtn.disabled = false; }, 2200);
        return;
      }
      if (labelEl) labelEl.innerHTML = '✓ Added';
      await refreshCart();
      openCart();
      setTimeout(() => { if (labelEl) labelEl.innerHTML = originalLabel; if (submitBtn) submitBtn.disabled = false; }, 1500);
    } catch (err) {
      console.error('[PI] add-to-cart failed', err);
      if (labelEl) labelEl.innerHTML = originalLabel;
      if (submitBtn) submitBtn.disabled = false;
    }
  });

  async function refreshCart() {
    try {
      const res = await fetch('/cart.js', { headers: { 'Accept': 'application/json' } });
      const cart = await res.json();
      // Update bag bubble count
      document.querySelectorAll('#cart-icon-bubble .pi-cart-bubble, #cart-icon-bubble').forEach(el => {
        if (el.classList.contains('pi-cart-bubble')) {
          el.textContent = cart.item_count;
          el.style.display = cart.item_count > 0 ? '' : 'none';
        } else if (!el.querySelector('.pi-cart-bubble') && cart.item_count > 0) {
          const span = document.createElement('span');
          span.className = 'pi-cart-bubble';
          span.textContent = cart.item_count;
          el.appendChild(span);
        }
      });
      // Refresh cart drawer body via section render
      try {
        const wasOpen = document.querySelector('.pi-cart-drawer.is-open') != null;
        const sectionRes = await fetch('/?section_id=pi-cart-drawer');
        if (sectionRes.ok) {
          const html = await sectionRes.text();
          const div = document.createElement('div');
          div.innerHTML = html;
          const newDrawer = div.querySelector('.pi-cart-drawer-root');
          const oldDrawer = document.querySelector('.pi-cart-drawer-root');
          if (newDrawer && oldDrawer) {
            oldDrawer.replaceWith(newDrawer);
            if (wasOpen) {
              newDrawer.querySelector('.pi-cart-drawer')?.classList.add('is-open');
              newDrawer.querySelector('.pi-cart-backdrop')?.classList.add('is-open');
            }
          }
        }
      } catch (_) {}

      // Refresh cart page contents (when shopper is on /cart)
      try {
        const oldPage = document.querySelector('.pi-cart-page-root');
        if (oldPage) {
          const pageRes = await fetch('/cart?section_id=pi-main-cart');
          if (pageRes.ok) {
            const html = await pageRes.text();
            const div = document.createElement('div');
            div.innerHTML = html;
            const newPage = div.querySelector('.pi-cart-page-root');
            if (newPage) oldPage.replaceWith(newPage);
          }
        }
      } catch (_) {}
    } catch (err) {
      console.error('[PI] refresh cart failed', err);
    }
  }

  // ─── PDP gallery thumbnail swap (delegated) ───────────────────
  document.addEventListener('click', (e) => {
    const thumb = e.target.closest('[data-pi-pdp-thumb]');
    if (!thumb) return;
    e.preventDefault();
    const wrap = thumb.closest('[data-pi-pdp-gallery]');
    if (!wrap) return;
    const mainImg = wrap.querySelector('[data-pi-pdp-main]');
    const newSrc = thumb.dataset.full;
    const newLabel = thumb.dataset.label || '';
    if (mainImg && newSrc) {
      if (mainImg.tagName === 'IMG') mainImg.src = newSrc;
      else mainImg.style.backgroundImage = 'url(' + newSrc + ')';
    }
    if (mainImg) mainImg.dataset.label = newLabel;
    wrap.querySelectorAll('[data-pi-pdp-thumb]').forEach(t => t.classList.remove('is-active'));
    thumb.classList.add('is-active');
  });
})();
